import { PdfGen, PdfUnit, PdfRect, PageGen } from '../../pdf/pdf';
import { ILayoutBaseGen } from '../layoutBase';
import { OptsBase } from '../optsBase';
import '../../pdf/draw/primitives';
import '../../pdf/draw/string';
import { GUtil } from '../../pdf/gutil';
import { IArchiveUrls, Random } from '@educraft/base';
import { MercatorMap, MapRenderer, Place, PointGps } from '../../pdf/map';
import { GLabel, GTuple } from '../../pdf/glabel';
import {
  ELogicoVariant,
  ELogicoMode,
  LogicoData,
  genMap,
  logicoCount,
  logicoSize,
  drawLogicoCircle,
  drawLogico,
} from '../../pdf/board/logico';

/**
 * Base class for Logico map generators
 * Displays a map with randomly selected places marked with Logico circles
 */
export class LogicoMapBase implements ILayoutBaseGen {
  protected mapData: MercatorMap | null = null;

  constructor(
    protected readonly variant: ELogicoVariant,
    protected readonly pack: string,
    protected readonly places: Place[]
  ) {}

  getRequiredArchiveUrls(): string[] {
    return [this.pack];
  }

  public async generate(pdf: PdfGen, opts: OptsBase): Promise<void> {
    // Load map from archive
    this.mapData = MercatorMap.loadFromArchive(opts.data.archive, this.pack);

    if (!this.mapData) {
      throw new Error('Failed to load map data from archive.');
    }

    const page1 = pdf.addPage();
    const page2 = pdf.addPage();

    const random = opts.data?.random ?? new Random();
    const answerCount = logicoCount(this.variant);
    const map = genMap(answerCount, random);

    const renderer = new MapRenderer(this.mapData);
    await renderer.preload(pdf);
    const size = logicoSize(this.variant);

    // Pick random places from the available places
    const items = renderer.pickRandomPlaces(
      this.places,
      answerCount,
      new PdfRect(0, 0, size.width, size.height),
      random,
      1 // minimum distance in cm
    );

    if (items.length < answerCount) {
      console.warn(`Only found ${items.length} places, need ${answerCount}`);
    }

    const labels: GTuple[] = items.map((item) => ({
      left: GLabel.fromText(item.name),
      right: GLabel.fromText(item.name),
    }));

    const drawQuestion = (data: LogicoData) => {
      // Draw the map
      renderer.drawMap(data.page, data.rect);

      // Draw the Logico circles at each place
      const gpsPoints = items.map((item) => item.gps);
      const points = renderer.pointFromGps(data.rect, gpsPoints);

      for (let index = 0; index < items.length; index++) {
        const point = points[index];
        const circleRect = new PdfRect(
          point.x - PdfUnit.FromMillimeters(2.5),
          point.y - PdfUnit.FromMillimeters(2.5),
          PdfUnit.FromMillimeters(5),
          PdfUnit.FromMillimeters(5)
        );
        drawLogicoCircle(data.page, data.variant, map[index], circleRect);
      }
    };

    drawLogico(pdf, this.variant, ELogicoMode.One, labels, map, {
      id: this.constructor.name,
      drawQuestion,
      data: opts.data,
    });
  }
}
